// Реалтайм приложения курьера: общий чат с руководителем и другими курьерами,
// плюс сигнал «смену закрыл руководитель». Один Socket.IO на всё приложение.

import { io, type Socket } from 'socket.io-client';

import { apiGet, refreshAccessToken } from '@/lib/api';
import { useAuthStore } from '@/store/auth';
import { useChatStore } from './store';
import { getChatMeId } from './me';

export { useChatStore } from './store';

export type ChatAttachment =
  | { type: 'photo'; url: string; width: number | null; height: number | null }
  | { type: 'voice'; url: string; mime: string; durationMs: number; waveform: number[] };

export interface ChatReply {
  id: string;
  authorId: string | null;
  authorName: string;
  text: string;
  attachment: { type: 'photo'; url: string } | { type: 'voice'; durationMs: number } | null;
  deleted: boolean;
}

export type ChatRole = 'owner' | 'courier';

export interface ChatMessage {
  id: string;
  text: string;
  createdAt: string;
  author: { id: string | null; name: string; role: ChatRole; avatarUrl?: string | null };
  attachment?: ChatAttachment | null;
  deleted?: boolean;
  replyTo?: ChatReply | null;
}

export function markDeleted(messages: ChatMessage[], id: string): ChatMessage[] {
  return messages.map((m) => {
    if (m.id === id) return { ...m, text: '', attachment: null, replyTo: null, deleted: true };
    if (m.replyTo?.id === id) return { ...m, replyTo: { ...m.replyTo, text: '', attachment: null, deleted: true } };
    return m;
  });
}

const SOCKET_URL = import.meta.env.VITE_API_URL?.trim() || window.location.origin;
const MAX_AUTH_RETRIES = 3;

let socket: Socket | null = null;
let chatOpen = false;
let authRetries = 0;
const messageListeners = new Set<(m: ChatMessage) => void>();
const deleteListeners = new Set<(id: string) => void>();
const hideListeners = new Set<(id: string) => void>();
const shiftListeners = new Set<(p: { shiftId: string; by: string }) => void>();
const loadListeners = new Set<(p: { loadId: string }) => void>();
const reconnectListeners = new Set<() => void>();

export async function refreshUnread(): Promise<void> {
  try {
    const { unread } = await apiGet<{ unread: number }>('/chat/unread', { silent: true });
    useChatStore.getState().setUnread(unread);
  } catch {
    /* следующая попытка — при переподключении сокета */
  }
}

function chatVisible(): boolean {
  return chatOpen && document.visibilityState === 'visible';
}

export function connectRealtime(): void {
  if (socket) return;
  const s = io(SOCKET_URL, {
    path: '/socket.io',
    transports: ['websocket'],
    reconnectionDelay: 1_000,
    reconnectionDelayMax: 8_000,
    auth: (cb) => cb({ token: useAuthStore.getState().accessToken ?? '', kind: 'courier' }),
  });
  socket = s;

  s.on('connect', () => {
    authRetries = 0;
    if (chatVisible()) s.emit('wh-chat:presence', { open: true });
    void refreshUnread();
    reconnectListeners.forEach((fn) => fn());
  });

  s.on('connect_error', () => {
    if (s.active || authRetries >= MAX_AUTH_RETRIES) return;
    authRetries += 1;
    void refreshAccessToken().then((token) => {
      if (token && socket === s) window.setTimeout(() => s.connect(), 1000 * authRetries);
    });
  });

  s.on('wh-chat:message', (m: ChatMessage) => {
    if (m.author.id !== getChatMeId() && !chatVisible()) {
      const { unread, setUnread } = useChatStore.getState();
      setUnread(unread + 1);
    }
    messageListeners.forEach((fn) => fn(m));
  });

  s.on('wh-chat:delete', (p: { id: string }) => {
    deleteListeners.forEach((fn) => fn(p.id));
    if (!chatVisible()) void refreshUnread();
  });

  s.on('wh-chat:hide', (p: { id: string }) => {
    hideListeners.forEach((fn) => fn(p.id));
  });

  s.on('courier:shift-finished', (p: { shiftId: string; by: string }) => {
    shiftListeners.forEach((fn) => fn(p));
  });

  s.on('courier:load', (p: { loadId: string }) => {
    loadListeners.forEach((fn) => fn(p));
  });
}

export function disconnectRealtime(): void {
  socket?.disconnect();
  socket = null;
  chatOpen = false;
  useChatStore.getState().setUnread(0);
}

export function setChatPresence(open: boolean): void {
  chatOpen = open;
  socket?.emit('wh-chat:presence', { open: open && document.visibilityState === 'visible' });
}

export function onChatMessage(fn: (m: ChatMessage) => void): () => void {
  messageListeners.add(fn);
  return () => messageListeners.delete(fn);
}

export function onChatDelete(fn: (id: string) => void): () => void {
  deleteListeners.add(fn);
  return () => deleteListeners.delete(fn);
}

export function onChatHide(fn: (id: string) => void): () => void {
  hideListeners.add(fn);
  return () => hideListeners.delete(fn);
}

/** Смену закрыли не вы (руководитель или автоматически). */
export function onShiftFinished(fn: (p: { shiftId: string; by: string }) => void): () => void {
  shiftListeners.add(fn);
  return () => shiftListeners.delete(fn);
}

/** Сокет снова на связи — повод отправить очередь и обновить экран. */
export function onReconnect(fn: () => void): () => void {
  reconnectListeners.add(fn);
  return () => reconnectListeners.delete(fn);
}

/** Руководитель выдал, изменил или отменил накладную. */
export function onLoadChanged(fn: (p: { loadId: string }) => void): () => void {
  loadListeners.add(fn);
  return () => loadListeners.delete(fn);
}
